// Public (unlisted) drawing board at /draw. Submitting can include optional
// artwork metadata, then saves the drawing to the generic drawings bucket.

import React, { useEffect, useState } from 'react';
import { DrawingCanvas } from '@/components/drawing';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { getRecentDrawings, submitDrawing } from '@/transport/api/publicApi';
import { cn } from '@/utilities/cn';
import { getCharCounterClass } from '@/utilities/inputFeedback';
import SEO from '../seo/SEO';

interface SavedDrawing {
  url: string;
  fileName: string;
  title?: string;
  from?: string;
}

const TITLE_MAX_LENGTH = 24;
const BY_MAX_LENGTH = 16;
const RECENT_DRAWINGS_LIMIT = 8;

function getDrawingTitle(drawing: SavedDrawing): string {
  return drawing.title || 'Unknown masterpiece';
}

function getDrawingArtist(drawing: SavedDrawing): string {
  return drawing.from || 'Anonymous';
}

function getDrawingCaption(drawing: SavedDrawing): string {
  return `${getDrawingTitle(drawing)} by ${getDrawingArtist(drawing)}`;
}

function mergeRecentDrawings(
  first: SavedDrawing[],
  second: SavedDrawing[]
): SavedDrawing[] {
  const seen = new Set<string>();
  return [...first, ...second]
    .filter((drawing) => {
      if (seen.has(drawing.fileName)) return false;
      seen.add(drawing.fileName);
      return true;
    })
    .slice(0, RECENT_DRAWINGS_LIMIT);
}

const Draw: React.FC = () => {
  const [pendingImage, setPendingImage] = useState<Blob | null>(null);
  const [drawingTitle, setDrawingTitle] = useState('');
  const [drawingFrom, setDrawingFrom] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedDrawings, setSavedDrawings] = useState<SavedDrawing[]>([]);
  const [selectedDrawing, setSelectedDrawing] = useState<SavedDrawing | null>(
    null
  );

  useEffect(() => {
    let isMounted = true;

    getRecentDrawings()
      .then(({ data }) => {
        if (!isMounted) return;
        const recentDrawings = data.drawings.map((drawing) => ({
          url: drawing.signedUrl,
          fileName: drawing.fileName,
          ...(drawing.title ? { title: drawing.title } : {}),
          ...(drawing.from ? { from: drawing.from } : {}),
        }));
        setSavedDrawings((current) =>
          mergeRecentDrawings(current, recentDrawings)
        );
      })
      .catch(() => {
        // Keep the drawing board usable even if the public gallery cannot load.
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubmit = (image: Blob) => {
    setSaveError(null);
    setDrawingTitle('');
    setDrawingFrom('');
    setPendingImage(image);
  };

  const handleSave = async () => {
    if (!pendingImage) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      const title = drawingTitle.trim();
      const from = drawingFrom.trim();
      const {
        data: { fileName, signedUrl },
      } = await submitDrawing(pendingImage, { title, from });
      setSavedDrawings((current) =>
        mergeRecentDrawings(
          [
            {
              url: signedUrl,
              fileName,
              ...(title ? { title } : {}),
              ...(from ? { from } : {}),
            },
          ],
          current
        )
      );
      setPendingImage(null);
      setDrawingTitle('');
      setDrawingFrom('');
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : 'Failed to save drawing'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleSave();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !isSaving) {
      setPendingImage(null);
      setDrawingTitle('');
      setDrawingFrom('');
    }
  };

  return (
    <>
      <SEO />
      <main className="mx-auto flex min-h-svh w-full max-w-2xl flex-col gap-4 px-4 pt-28 pb-12 md:pt-32">
        <h1 className="text-lg font-medium">Draw something for Poppy!</h1>
        <DrawingCanvas aspectRatio={4 / 3} onSubmit={handleSubmit} />
        {savedDrawings.length > 0 && (
          <section
            className="flex flex-col gap-2"
            aria-labelledby="saved-drawings"
          >
            <h2 id="saved-drawings" className="text-sm font-medium">
              Recently submitted
            </h2>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
              {savedDrawings.map((drawing) => {
                const caption = getDrawingCaption(drawing);
                return (
                  <figure key={drawing.fileName} className="min-w-0">
                    <button
                      type="button"
                      className="group block w-full rounded-md text-left outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
                      aria-label={`Open ${caption} preview`}
                      onClick={() => setSelectedDrawing(drawing)}
                    >
                      <span className="block aspect-[4/3] w-full overflow-hidden rounded-md ring-1 ring-foreground/10">
                        <img
                          src={drawing.url}
                          alt={caption}
                          className="size-full object-cover transition-transform duration-150 group-hover:scale-[1.04] group-focus-visible:scale-[1.04]"
                        />
                      </span>
                    </button>
                    <figcaption className="mt-1 line-clamp-2 min-h-8 break-words text-xs leading-4 text-muted-foreground">
                      {caption}
                    </figcaption>
                  </figure>
                );
              })}
            </div>
          </section>
        )}
        <Dialog open={pendingImage !== null} onOpenChange={handleOpenChange}>
          <DialogContent showCloseButton={!isSaving}>
            <form className="grid gap-4" onSubmit={handleSaveSubmit}>
              <DialogHeader>
                <DialogTitle>Thanks for the artwork</DialogTitle>
                <DialogDescription>
                  We are putting it on our fridge right now. Want to sign the
                  masterpiece first?
                </DialogDescription>
              </DialogHeader>
              <FieldGroup>
                <Field>
                  <div className="flex w-full items-center justify-between gap-3">
                    <FieldLabel htmlFor="drawing-title">
                      Title (optional)
                    </FieldLabel>
                    <div className="text-xs leading-none">
                      <span
                        className={cn(
                          getCharCounterClass(
                            drawingTitle.length,
                            TITLE_MAX_LENGTH
                          )
                        )}
                      >
                        {drawingTitle.length}/{TITLE_MAX_LENGTH}
                      </span>
                    </div>
                  </div>
                  <Input
                    id="drawing-title"
                    value={drawingTitle}
                    maxLength={TITLE_MAX_LENGTH}
                    placeholder="A beautiful masterpiece"
                    disabled={isSaving}
                    onChange={(event) => setDrawingTitle(event.target.value)}
                  />
                </Field>
                <Field>
                  <div className="flex w-full items-center justify-between gap-3">
                    <FieldLabel htmlFor="drawing-from">
                      By (optional)
                    </FieldLabel>
                    <div className="text-xs leading-none">
                      <span
                        className={cn(
                          getCharCounterClass(drawingFrom.length, BY_MAX_LENGTH)
                        )}
                      >
                        {drawingFrom.length}/{BY_MAX_LENGTH}
                      </span>
                    </div>
                  </div>
                  <Input
                    id="drawing-from"
                    value={drawingFrom}
                    maxLength={BY_MAX_LENGTH}
                    placeholder="A mysterious genius"
                    disabled={isSaving}
                    onChange={(event) => setDrawingFrom(event.target.value)}
                  />
                </Field>
              </FieldGroup>
              {saveError ? (
                <p className="text-sm text-destructive" role="alert">
                  {saveError}
                </p>
              ) : null}
              <DialogFooter>
                <DialogClose asChild>
                  <Button type="button" variant="outline" disabled={isSaving}>
                    Cancel
                  </Button>
                </DialogClose>
                <Button type="submit" disabled={isSaving}>
                  {isSaving ? (
                    <Spinner className="size-4 text-primary-foreground" />
                  ) : (
                    'Submit'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        <Dialog
          open={selectedDrawing !== null}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) {
              setSelectedDrawing(null);
            }
          }}
        >
          <DialogContent className="max-h-[calc(100svh-2rem)] max-w-[calc(100vw-2rem)] sm:max-w-5xl lg:max-w-6xl">
            {selectedDrawing ? (
              <>
                <DialogHeader>
                  <DialogTitle>{getDrawingTitle(selectedDrawing)}</DialogTitle>
                  <DialogDescription>
                    By {getDrawingArtist(selectedDrawing)}
                  </DialogDescription>
                </DialogHeader>
                <img
                  src={selectedDrawing.url}
                  alt={getDrawingCaption(selectedDrawing)}
                  className="max-h-[calc(100svh-9rem)] w-full rounded-lg object-contain ring-1 ring-foreground/10"
                />
              </>
            ) : null}
          </DialogContent>
        </Dialog>
      </main>
    </>
  );
};

export default Draw;
